import { createHttpClient } from '@adapters/HttpClient';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Combobox } from '@shared/components/ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PackApiAdapter } from '../../adapters/PackApiAdapter';
import { ShopApiAdapter } from '../../adapters/ShopApiAdapter';
import { Pack } from '../../models/Pack';
import { CHANNELS_WITH_FILE_IMPORT } from '@modules/Shops/List/models/Shops';

const AMAZON_FBM_CHANNEL_ID = CHANNELS_WITH_FILE_IMPORT[0];

export interface CreateLinkPackModalRef {
  openWithPack: (pack: Pack, onSuccess?: () => void) => void;
}

const CreateLinkPackModal = forwardRef<CreateLinkPackModalRef>((_props, ref) => {
  const { t } = useTranslation('packsModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const { toast } = useToast();
  const onSuccessRef = useRef<(() => void) | undefined>();

  const [pack, setPack] = useState<Pack | null>(null);
  const [tienda, setTienda] = useState('');
  const [productoSel, setProductoSel] = useState('');
  const [variante, setVariante] = useState('');
  const [shops, setShops] = useState<{ value: string; label: string; shopObj?: any }[]>([]);
  const [loadingShops, setLoadingShops] = useState(false);
  const [productos, setProductos] = useState<
    { value: string; label: string; variant_id?: string | number; variants?: Array<{ id: number; title: string }> }[]
  >([]);
  const [loadingProductos, setLoadingProductos] = useState(false);
  const [variantes, setVariantes] = useState<{ value: string; label: string }[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [skuAmazon, setSkuAmazon] = useState('');

  const selectedShop = shops.find((s) => s.value === tienda);
  const isAmazonFbm = selectedShop?.shopObj?.channel_id === AMAZON_FBM_CHANNEL_ID;

  // Cargar tiendas al montar
  useEffect(() => {
    setLoadingShops(true);
    const httpClient = createHttpClient(apiClientWH);
    const adapter = new ShopApiAdapter(httpClient);
    adapter.getAll().then((data) => {
      setShops(
        data.map((shop) => ({
          value: shop.id,
          label: shop.name,
          shopObj: shop,
        })),
      );
      setLoadingShops(false);
    });
  }, []);

  // Cargar productos cuando cambia la tienda (no para Amazon FBM)
  useEffect(() => {
    if (tienda && !isAmazonFbm) {
      setLoadingProductos(true);
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new ShopApiAdapter(httpClient);
      adapter.getProductsByShop(tienda).then((data) => {
        setProductos(data.map((prod) => ({ value: prod.id, label: prod.name, ...prod })));
        setLoadingProductos(false);
      });
    } else {
      setProductos([]);
    }
    setProductoSel('');
    setVariantes([]);
    setSkuAmazon('');
  }, [tienda, isAmazonFbm]);

  // Extraer variantes del producto seleccionado
  useEffect(() => {
    if (productoSel) {
      const productoSeleccionado = productos.find((p) => p.value === productoSel);
      if (productoSeleccionado?.variants && productoSeleccionado.variants.length > 0) {
        setVariantes(
          productoSeleccionado.variants.map((v) => ({
            value: String(v.id),
            label: v.title,
          })),
        );
      } else {
        setVariantes([]);
      }
    } else {
      setVariantes([]);
    }
    setVariante('');
  }, [productoSel, productos]);

  useImperativeHandle(ref, () => ({
    openWithPack: (packData: Pack, onSuccess?: () => void) => {
      setPack(packData);
      onSuccessRef.current = onSuccess;
      setTienda('');
      setProductoSel('');
      setVariante('');
      setSkuAmazon('');
      setProductos([]);
      setVariantes([]);
      modalRef.current?.openModal();
    },
  }));

  const handleCreateLink = async () => {
    const productoSeleccionado = productos.find((p) => p.value === productoSel);
    const tieneVariantes = productoSeleccionado?.variants && productoSeleccionado.variants.length > 0;
    if (isAmazonFbm) {
      if (!pack || !tienda || !skuAmazon.trim() || isSubmitting) return;
    } else {
      if (!pack || !tienda || !productoSel || (tieneVariantes && !variante) || isSubmitting) return;
    }

    setIsSubmitting(true);
    try {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new PackApiAdapter(httpClient);

      if (isAmazonFbm) {
        await adapter.linkAmazonSku({
          packId: pack.id,
          externalVariantId: skuAmazon.trim(),
          shopId: Number(tienda),
        });
      } else {
        const payload: any = {
          pack_id: pack.id,
          external_product_id: productoSel,
          shop_id: tienda,
        };
        if (variante) payload.external_variant_id = variante;
        else if (productoSeleccionado?.variant_id) payload.external_variant_id = productoSeleccionado.variant_id;
        await adapter.createLink(payload);
      }

      toast({
        title: t('link.success_title'),
        description: t('link.success_description'),
        variant: 'success',
      });

      // 🎯 Actualizar datos en segundo plano (refetch de packs y refresh del pack en modal de enlaces)
      onSuccessRef.current?.();

      // 🎯 Cerrar el modal de crear enlace para volver al modal de enlaces
      modalRef.current?.closeModal();
    } catch (error: any) {
      const errorData = error?.response?.data;

      let errorMsg;
      if (error?.response?.status === 409 || (typeof error?.message === 'string' && error.message.includes('409'))) {
        errorMsg = 'El enlace ya existe';
      } else {
        errorMsg = errorData?.error?.message || errorData?.message || error?.message || t('link.error_description');
      }

      toast({
        title: t('link.error_title'),
        description: errorMsg,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalCenter
      ref={modalRef}
      title={t('link.create_link_title')}
      type="default"
      width="lg"
      className="w-full h-full sm:w-[579px] sm:h-auto"
    >
      <div className="flex flex-col h-full">
        <div className="flex-1 space-y-6 mt-1 pb-4">
          {/* Información del pack */}
          {pack && (
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium text-foreground">{pack.title}</p>
            </div>
          )}

          <div>
            <label className="block text-sm mb-2 text-text">{t('link.shop_label')}</label>
            <Select value={tienda} onValueChange={setTienda} disabled={loadingShops || isSubmitting}>
              <SelectTrigger data-testid="create-link-shop-select">
                <SelectValue placeholder={t('link.shop_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {shops.map((u) => (
                  <SelectItem key={u.value} value={u.value}>
                    {u.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {tienda && isAmazonFbm && (
            <div>
              <label className="block text-sm mb-2 text-text">{t('link.sku_amazon_label')}</label>
              <Input
                value={skuAmazon}
                onChange={(e) => setSkuAmazon(e.target.value)}
                placeholder={t('link.sku_amazon_placeholder')}
                disabled={isSubmitting}
                data-testid="create-link-sku-amazon-input"
              />
            </div>
          )}
          {tienda && !isAmazonFbm && (
            <div>
              <label className="block text-sm mb-2 text-text">{t('link.product_label')}</label>
              <Combobox
                options={productos}
                value={productoSel}
                setValue={setProductoSel}
                placeholder={t('link.product_placeholder')}
                disabled={loadingProductos || isSubmitting}
                dataTestId="create-link-product-select"
                translations={{ notFound: t('link.product_not_found') }}
              />
            </div>
          )}
          {tienda && !isAmazonFbm && productoSel && variantes.length > 0 && (
            <div>
              <label className="block text-sm mb-2 text-text">{t('link.variant_label')}</label>
              <Combobox
                options={variantes}
                value={variante}
                setValue={setVariante}
                placeholder={t('link.variant_placeholder')}
                disabled={isSubmitting}
                dataTestId="create-link-variant-select"
                translations={{ notFound: t('link.variant_not_found') }}
              />
            </div>
          )}
        </div>

        {/* Botonera */}
        <div className="flex justify-end gap-2 pt-4">
          <Button
            variant="outline"
            size="lg"
            onClick={() => modalRef.current?.closeModal()}
            disabled={isSubmitting}
            data-testid="cancel-create-link-button"
          >
            {t('button.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleCreateLink}
            disabled={
              !tienda ||
              (isAmazonFbm ? !skuAmazon.trim() : !productoSel || (variantes.length > 0 && !variante)) ||
              isSubmitting
            }
            data-testid="confirm-create-link-button"
          >
            {isSubmitting ? t('button.creating') : t('link.create_link')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

CreateLinkPackModal.displayName = 'CreateLinkPackModal';

export default CreateLinkPackModal;
